/**
 * audiences.js — the three tellings of the home page.
 *
 * Oct 2026 (HOMEPAGE_AUDIENCE_PATHS.md) — Randy: *"These are not separate
 * products. They are three marketing views of My Silo."* Same page, same
 * sections, same order, same lengths — only the words change, so switching
 * reads as the page speaking to someone else rather than a different site.
 *
 * `anyone` is the default and the baseline: it is today's copy, word for word.
 * Nothing moves until a visitor picks a card, and picking Anyone comes home.
 *
 * Two small conventions keep JSX out of this file:
 *   • `heroStrong` names a substring of `heroBody` to highlight.
 *   • `{use}` inside a value-card body is replaced by the "use" link that
 *     opens the Unique Ways modal.
 */

// The two hero doorways are the SAME in all three tellings — Randy, Oct 22:
// *"change them all to match the default verbiage, for those two cards."*
const DEFAULT_DOORS = {
  fan: {
    eyebrow: "Free",
    title: "Join as a fan",
    body: "Browse the growing personas and digital twins landing on My Silo. Find silos of interest, ask them anything. Some creators open theirs free, some charge a monthly fee — the portal is yours either way.",
  },
  creator: {
    eyebrow: "For experts & creators",
    title: "Create a silo",
    body: "Turn your archive into a twin that answers only from your knowledge. Pick how you start — subscribe straight away, or try it first — on the next screen.",
  },
};

export const AUDIENCE_KEYS = ["youtube", "business", "anyone"];

export const AUDIENCES = {
  /* ── My Silo for YouTube ─────────────────────────────────────────────────
     Source: the "Your YouTube Digital Twin" entry in `waysToUse.js`, which is
     Randy's own account of what this is for. The product is the twin ANSWERING
     IN THE COMMENT SECTION — his links back in circulation, his own videos
     suggested, his voice. Pointing fans at a public page is a workaround for
     someone postponing the purchase; it is not marketing and is not here. */
  youtube: {
    key: "youtube",
    label: "My Silo for YouTube",
    short: "YouTube",
    switchLabel: "YouTubers",
    cardLine: "Finally, a real assistant that can actually answer in your voice. Refer your other videos, and repurpose your affiliate links.",
    tone: "youtube",
    eyebrow: "Closed-corpus AI · Built from your channel",
    h1: { pre: "The", accent: "accuracy of you,", post: "for your channel" },
    h1Size: "text-4xl sm:text-5xl lg:text-6xl",
    h1Sub: "Never miss a reply. Answered solely by your data. Pointed directly at your YouTube comments section. Users ask there, Your YouTube digital Twin, Answers there!",
    heroMark: "youtube",
    heroBody:
      "My Silo turns your transcripts, descriptions and years of comment replies into a twin that answers only from your channel — in your voice, with your links, to your videos. Set on auto pilot or approve replies before they post.",
    heroStrong: "only from your channel",
    doors: DEFAULT_DOORS,
    values: [
      {
        icon: "shield",
        title: "It answers from your videos. Only yours.",
        body: "No invented advice under your name. Every reply is grounded in your own transcripts, descriptions and past answers, and politely refuses anything you never covered.",
      },
      {
        icon: "money",
        title: "Your links, back in circulation.",
        body: "Every affiliate and product link you ever buried in a description is inventory. Your twin brings the right one into the answer it belongs in, by its real name.",
      },
      {
        icon: "sparkle",
        title: "One twin, every repeat question.",
        body: "The same question four hundred times, answered properly once — and there are countless other ways to {use} My Silo.",
      },
      {
        icon: "lock",
        title: "Your channel stays yours.",
        body: "Transcripts, comments and keys are walled off and encrypted. Your work never trains anyone else's model, and nothing reaches YouTube without your approval.",
      },
    ],
    stepsEyebrow: "How it works",
    stepsH2: { pre: "Four steps from your back catalogue to", accent: "a twin that replies." },
    steps: [
      { n: "01", t: "Sweep the channel", b: "Point the macro at your uploads. Transcripts, descriptions, affiliate links and your own answered comments land in the silo in one pass." },
      { n: "02", t: "Teach it your voice", b: "Tone Architect and length control set how an answer opens, how long it runs and how it signs off — you on your phone, not a support bot." },
      { n: "03", t: "Let it draft replies", b: "New comments arrive as drafted answers with the right links already attached. Read, tweak, post. Nothing goes out unapproved." },
      { n: "04", t: "Watch what they ask", b: "Every question routes back into the silo, and Probe shows what your audience keeps asking — your next video, straight from demand." },
    ],
    tools: {
      eyebrow: "The YouTube Digital Twin",
      h2: { pre: "One channel.", accent: "Every answer, reusable." },
      body: "Your twin knows the two or three videos you already made on the topic and can point at them by their real titles. It knows which description link belongs with which question. It drafts in your voice, signs from your channel, and sends every fan question back into the silo — from ingest to inbox, the loop closes.",
      tiles: [
        "Relevant links — the right URL, named properly, inside the answer it belongs in",
        "Video suggestions — point at your own uploads instead of typing it again",
        "Comment inbox — drafted replies waiting for your approval, never auto-posted",
      ],
    },
    // One proof only, and it is his own: the cosmic silo has nothing to do with
    // a channel. The second button goes to the real YouTube channel so a visitor
    // can read the twin's replies where they actually live.
    demos: {
      kind: "direct",
      slug: "east-tactics",
      label: "See a live silo",
      channelHref: "https://www.youtube.com/@EastTactics/videos?view=0&sort=p&shelf_id=5",
      channelLabel: "East Tactics on YouTube",
    },
    cta: { pre: "Your channel already answered it.", accent: "Let it answer again.", button: "Build your channel's twin" },
    pricingIntro: "Same membership for a channel. The YouTube tools are a one-time upgrade, not a subscription.",
  },

  /* ── My Silo for Business ────────────────────────────────────────────────
     Randy's steer: *"Silo Anywhere should be a major focus of the Business
     version… the ability for the widget to incorporate multiple agents."*

     Rewritten Oct 22 on his second pass. Struck out: manuals, policies, case
     studies, departments, agencies — *"I don't like the words."* The frame he
     gave instead, drawn from his own pictures (a practice where each person
     introduces themselves and their training, a salon where every chair has its
     own hours and specialities, a salesman keeping his own list of what is on
     the lot, a merchant answering package questions with the checkout link, a
     figure who would rather answer in his own voice than hand over a three-page
     report): **everyone who answers for the business gets a twin of their own,
     in the corner of the site they already have.** His note — *"these examples
     shouldn't be expressed explicitly… ideally we're looking for a universal
     expression"* — so none of them is named. */
  business: {
    key: "business",
    label: "My Silo for Business",
    short: "Business",
    switchLabel: "Businesses",
    cardLine: "Same cost, countless ways to use your silo, as a widget, on your Company website.",
    tone: "business",
    eyebrow: "Closed-corpus AI · Owned by you",
    h1: { pre: "The", accent: "accuracy of you,", post: "for your business" },
    h1Size: "text-4xl sm:text-5xl lg:text-6xl",
    h1Sub: "Add a My Silo assistant Widget to \u201cyour\u201d website that speaks EXACTLY in their voice.",
    heroBody:
      "Everyone who answers for your business can have a twin of their own — Share, what they do, their own links, when they are free, the questions they field ten times a week — speaking only from what they put in it, in their own voice, in the corner of your own site.",
    heroStrong: "in their own voice",
    doors: DEFAULT_DOORS,
    values: [
      {
        icon: "shield",
        title: "It answers from what you put in. Nothing else.",
        body: "No invented price, no promise you would never make, no guess about next Tuesday. Your twin speaks from what you gave it and says plainly when something is not in there.",
      },
      {
        icon: "code",
        title: "In the corner of your own site.",
        body: "One widget on your own pages, answering where people are already looking. Several twins can sit behind the one launcher and the visitor simply picks who to ask.",
      },
      {
        icon: "sparkle",
        title: "A twin for every person who answers.",
        body: "Their services, their hours, their specialities, their own way of explaining it — and there are countless other ways to {use} My Silo.",
      },
      {
        icon: "money",
        title: "Your voice, your links, at the moment of asking.",
        body: "The answer arrives the way you would say it, with the link that lets them book, buy or reach you while the question is still fresh.",
      },
    ],
    stepsEyebrow: "How it works",
    stepsH2: { pre: "Four steps from what you know to", accent: "an answer on your own page." },
    steps: [
      { n: "01", t: "Say what you would say", b: "Type it, talk it, or drop in whatever you already have written down. The way you explain a service to a regular is exactly what the twin needs." },
      { n: "02", t: "Give each person their own", b: "One twin per chair, per desk, per line. Their skills, their training, their days, their answers — nobody speaking on anyone else's behalf." },
      { n: "03", t: "Put it where you already are", b: "Silo Anywhere drops a launcher into the corner of your own pages, with as many twins behind it as the visitor might need." },
      { n: "04", t: "Keep it current, hear what's asked", b: "Change a price or an opening in the morning and it answers correctly by lunch — and Probe shows you what people keep coming back for." },
    ],
    tools: {
      eyebrow: "Silo Anywhere",
      h2: { pre: "Several people.", accent: "One corner of your site." },
      body: "A visitor opens the launcher on your own page and chooses who to ask: the one whose Thursdays are open, the one who knows what came in this morning, the one who knows which thing goes with which. Each twin answers from its own material, in its own voice, with the link that moves things along — and nothing crosses over between them. Change it at 9am and the answers change with it.",
      tiles: [
        "Multi-agent launcher — one embed, a twin for each person",
        "Scoped answers — every twin sees only its own material",
        "Edited in the morning, answered correctly by lunch",
      ],
    },
    // No examples on this telling. There is no business silo to show yet, and a
    // creator's twin is not a stand-in for one — so the button is absent, not
    // empty. It returns the day Randy builds or invites one.
    demos: { kind: "none" },
    cta: { pre: "Everything you would say,", accent: "said while you are busy.", button: "Build your first silo" },
    pricingIntro: "Same membership for a business — priced per silo, never per seat. Silo Anywhere and the Pro tools are one-time upgrades.",
  },
  anyone: {
    key: "anyone",
    label: "My Silo for Anyone",
    short: "Anyone",
    switchLabel: "Anyone",
    cardLine: "Experts, authors, coaches, hobbyists — anyone with knowledge worth keeping.",
    tone: "anyone",
    eyebrow: "Closed-corpus AI · Owned by you",
    h1: { pre: "The", accent: "accuracy of you,", post: "offered to the world." },
    // Oct 31 2026 (Randy) — was falling through to the larger default while the
    // other two faces set this, so Anyone's headline rendered a step bigger.
    // All three now share one size, which is the point of one shared headline.
    h1Size: "text-4xl sm:text-5xl lg:text-6xl",
    heroBody:
      "My Silo turns your writing, lectures, frameworks, and recordings into a private AI twin that answers only from your knowledge. Curate it flawlessly. Share it via a private link. Charge a monthly fee. Keep your voice. Keep your money.",
    heroStrong: "only from your knowledge",
    doors: DEFAULT_DOORS,
    values: [
      {
        icon: "shield",
        title: "Trained on you. Only you.",
        body: "No outside opinions, no hallucinated quotes. Every answer is grounded in your uploaded corpus and politely refuses anything beyond it.",
      },
      {
        icon: "money",
        title: "Earn from your expertise.",
        body: "Offer for free. Or set your own monthly subscription. Stripe handles it all. Supporters cover the platform fee, you keep 100% of your price.",
      },
      {
        icon: "sparkle",
        title: "One twin, endless uses.",
        body: "Coaching, teaching, fan Q&A, a private knowledge vault, lead capture — there are countless ways to {use} My Silo.",
      },
      {
        icon: "lock",
        title: "Your data is yours. Period.",
        body: "Your uploads and conversations are encrypted and walled off. Nobody — not even My Silo — can read your private content, and your knowledge never trains anyone else's model.",
      },
    ],
    stepsEyebrow: "How it works",
    stepsH2: { pre: "Four steps from your archive to", accent: "a paid digital twin." },
    steps: [
      { n: "01", t: "Upload everything", b: "PDFs, transcripts, books, notes — drop it all in. The Workbench is where you write, stage and commit the version worth keeping." },
      { n: "02", t: "Choose a template", b: "Academic, Tactical, or Modern. Your silo gets a beautiful page that matches your voice." },
      { n: "03", t: "Share your link", b: "Send /s/your-name to your audience. No feed. No algorithm. Found by name, not by ranking." },
      { n: "04", t: "Get paid monthly", b: "Set your price. Fans subscribe. Money lands in your Stripe account — supporters pay the platform fee on top, so you keep your full price." },
    ],
    tools: {
      eyebrow: "The Recursive Workbench",
      h2: { pre: "Three slots.", accent: "Infinite refinement." },
      body: "Drop raw transcripts into Slot 1. Trim the fat. Send the result to Slot 2. Condense it. Compare across slots. Commit only the cleanest version to your official silo. This is the curation gym champions train in.",
      tiles: [
        "Summarize — No matter the input size, you choose the output ratio",
        "Accurately Condense — densest possible footprint",
        "Compare — agreements, contradictions, novelty",
      ],
    },
    // Both proofs, in roster order: the deep corpus first, then the YouTube twin.
    demos: { kind: "choose" },
    cta: { pre: "Your knowledge already exists.", accent: "Make it answerable.", button: "Create your silo" },
    pricingIntro: "",
  },
};

// Photo tone. Randy: *"keep the exact same image, add an overlay filter…
// perhaps red for YouTube and blue for business?"* One hero photograph, three
// lights in the room.
export const TONE_RGB = {
  // The hue shift (blended) plus a plain wash on top so it is unmistakable.
  youtube: { blend: "rgba(193,39,45,0.45)", wash: "rgba(176,28,32,0.10)" },
  business: { blend: "rgba(31,95,179,0.60)", wash: "rgba(24,78,156,0.14)" },
  anyone: { blend: "transparent", wash: "transparent" },
};

// The accent the selector card itself carries when chosen, so the eye can see
// which telling it is reading without having to re-read the label.
